import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import { testimonials } from '@/mocks/movies';
import { useRef, useState } from 'react';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className="w-3 h-3 flex items-center justify-center">
          <i className={`text-xs ${i < rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-400'}`}></i>
        </span>
      ))}
    </div>
  );
}

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    if (honeypotField) {
      formData.delete('phone_alt');
    }

    try {
      setFormStatus('idle');
      setFormError('');

      const submitAddr = form.getAttribute('action');
      if (!submitAddr) {
        setFormStatus('error');
        setFormError('表单提交地址未配置');
        return;
      }

      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 神马影院 | 品牌介绍与联系方式"
        description="神马影院是一家专注于提供高清电影在线观看的平台。了解神马影院的品牌故事、用户口碑评价和联系方式，海量电影电视剧免费在线观看。"
        canonical="https://www.shenmayingyuan.com/about"
        schemaScripts={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "关于我们 - 神马影院",
            "description": "神马影院是一家专注于提供高清电影在线观看的平台。",
            "url": "https://www.shenmayingyuan.com/about",
            "isPartOf": {
              "@type": "WebSite",
              "name": "神马影院",
              "url": "https://www.shenmayingyuan.com"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.shenmayingyuan.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "关于我们",
                "item": "https://www.shenmayingyuan.com/about"
              }
            ]
          }
        ]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 min-h-screen">
        <div className="w-full px-4 md:px-6 pt-8 pb-12">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mt-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                关于神马影院
              </h1>
              <p className="text-sm text-foreground-500 mb-8">高清电影在线观看平台</p>

              <div className="prose prose-sm max-w-none">
                <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">品牌介绍</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    神马影院成立于2020年，是一家专注于在线影视服务的互联网平台。我们致力于为广大用户提供海量高清电影和电视剧的在线观看服务，
                    让每一位影迷都能随时随地享受高品质的观影体验。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    神马影院拥有丰富的影视资源库，涵盖<strong>动作片、喜剧片、爱情片、科幻片、恐怖片、动画片、剧情片</strong>等全类型影片。
                    我们采用先进的流媒体传输技术，确保每一部影片都能以<strong>高清画质</strong>流畅播放，真正做到<strong>即点即播、不缓冲、不卡顿</strong>。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    作为一个用户至上的平台，神马影院始终坚持"免费、高清、便捷"的服务理念。我们无需用户下载任何软件或插件，
                    只需打开浏览器即可在线观看。我们的内容团队全年无休地更新影片资源，确保热门电影和电视剧第一时间上线。
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-accent-50 rounded-lg p-5 text-center">
                    <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-movie-2-line text-xl"></i>
                    </span>
                    <h3 className="text-2xl font-heading font-bold text-accent-600 mb-1">10000+</h3>
                    <p className="text-xs text-foreground-600">高清影片资源</p>
                  </div>
                  <div className="bg-primary-50 rounded-lg p-5 text-center">
                    <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                      <i className="ri-user-heart-line text-xl"></i>
                    </span>
                    <h3 className="text-2xl font-heading font-bold text-primary-600 mb-1">500万+</h3>
                    <p className="text-xs text-foreground-600">累计服务用户</p>
                  </div>
                  <div className="bg-secondary-50 rounded-lg p-5 text-center">
                    <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                      <i className="ri-thumb-up-line text-xl"></i>
                    </span>
                    <h3 className="text-2xl font-heading font-bold text-secondary-600 mb-1">99.5%</h3>
                    <p className="text-xs text-foreground-600">用户好评率</p>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">用户评价</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {testimonials.map(t => (
                      <div key={t.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                        <div className="flex items-center gap-3 mb-3">
                          <img
                            src={t.avatar}
                            alt={t.author}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="text-sm font-medium text-foreground-800">{t.author}</p>
                            <StarRating rating={t.rating} />
                          </div>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                        <p className="text-xs text-foreground-400 mt-2">{t.date}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">联系我们</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-mail-line"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h3>
                        <p className="text-sm text-foreground-600">service@shenmayingyuan.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-phone-line"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800 mb-1">客服电话</h3>
                        <p className="text-sm text-foreground-600">400-888-8888</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                        <i className="ri-map-pin-line"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800 mb-1">公司地址</h3>
                        <p className="text-sm text-foreground-600">北京市朝阳区建国路88号</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-time-line"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800 mb-1">工作时间</h3>
                        <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div id="contact-form" className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-2">在线留言</h2>
                  <p className="text-sm text-foreground-500 mb-6">如有任何问题或建议，欢迎给我们留言</p>

                  <form ref={formRef} onSubmit={handleSubmit} action="https://readdy.ai/api/form/d9mtbucit35ii3jdllmg" data-readdy-form="">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="antispam-field" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors"
                        placeholder="请输入留言主题"
                      />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>

                    {formStatus === 'error' && formError && (
                      <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-600">
                        {formError}
                      </div>
                    )}
                    {formStatus === 'success' && (
                      <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-600">
                        感谢您的留言！我们会尽快回复您。
                      </div>
                    )}

                    <button
                      type="submit"
                      className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      提交留言
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}